import { reportError } from "utils/diagnostics";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Params } from "react-router-dom";
import TableRow from "@mui/material/TableRow";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckIcon from "@mui/icons-material/Check";
import { CircularProgress, Menu, MenuItem, Typography } from "@mui/material";
import { ErrorScreen } from "components/common/error -screen/error-screen.component";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { PageSection, DetailWrapper, ActionBar } from "components/common/Wrapper";
import CustomTable from "components/table/table.component";
import { ButtonText } from "components/common/Buttons";
import { TableBodyCell } from "components/table/table.style";
import { useRequest } from "hooks/useRequest";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { convertIpAddress, getDateString, storeActiveSerial } from "utils/util-functions";
import { Spinner } from "components/common/spinner/spinner.component";
import useConfirm from "hooks/useConfirm";
import DetailForm from "./components/form-component/device-detail-form.component";
import { SectionHeader } from "./components/form-component/DeviceDetail.style";
import {
  EVENTLOG_GRID_COLUMNS,
  PENDING_GRID_COLUMNS,
  ACTION_JSON,
  EI_GRID_COLUMNS,
  PendingGridColumnsTypes,
} from "./device-detail.config";
import { Popup } from "../../../components/common/popup/popup.component";
import { changeDeviceStatusFromUI, getDeviceStatus } from "../../../api/DeviceHelpers";
import {
  connectMissionControl,
  missionControlErrorMessage,
  MissionControlStatus,
} from "pages/launch-mission-control/mission-control.api";
import { MissionControlHelp } from "pages/launch-mission-control/mission-control-help.component";
import { showInfo, showSuccess, showWarning } from "../../../utils/notifications";
import { pollUntil } from "utils/general";
import { REFRESH_VALUES_POLL_INTERVAL, REFRESH_VALUES_POLL_TIMEOUT } from "utils/constants";
import { DeviceDetailDataTypes } from "../devices.config";
import { getTableCellValue } from "../utilities/device.utilities";
import { useAuth } from "hooks/useProvideAuth";
import { factoryResetSpeedwayDevice, resetSpeedwayPassword } from "./device-detail.api";
import { TableColumnTypes } from "pages/configs/configs";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { ConfigurationArchivesDialog } from "components/common/configuration-archives/configuration-archives.component";

const RELEASE_NOTES_URL = "https://airgain.com/products/megafi-2-hpue/";

export const VPN_CONNECTED = "Connected";
export const VPN_CONNECTION_FAILED = "Connection Failed";
export const VPN_ERROR = "Error";

export function DeviceDetailPage(): JSX.Element {
  const [getConfirmation, Confirmation] = useConfirm();
  const [isOpenArchivesDialog, setIsOpenArchivesDialog] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isControlLaunching, setIsControlLaunching] = useState<boolean>(false);
  const missionControlRequestRef = useRef<AbortController | null>(null);
  const [missionControlStatus, setMissionControlStatus] = useState<MissionControlStatus>();
  const [isError, setIsError] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");
  const { sn: serialNumber }: Readonly<Params<string>> = useParams();
  const navigate = useNavigate();
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPasswordReset, setIsOpen] = useState<boolean>(false);
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupBody, setPopupBody] = useState<string>("");
  const [popupBtnFunc, setPopupBtnFunc] = useState<(() => void) | null>(null);

  const [isRefreshPolling, setIsRefreshPolling] = useState<boolean>(false);

  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    setIsControlLaunching(false);
    setMissionControlStatus(undefined);
    setIsError(false);
    setErrorText("");
    return () => {
      isMountedRef.current = false;
      missionControlRequestRef.current?.abort();
      missionControlRequestRef.current = null;
    };
  }, [serialNumber]);

  const {
    data: detailData,
    isLoading: isGettingDetail,
    mutate: mutateDetail,
    error: detailError,
  } = useRequest(`eiStatusUIHook?serialNumber=${serialNumber}`);
  const { isDemoUser, isReadOnly }: any = useAuth();

  const columns = useMemo(
    () =>
      [
        {
          id: "type",
          label: "Type",
        },
        { id: "port", label: "Port" },
        { id: "rxBytes", label: "Received MB" },
        { id: "txBytes", label: "Transmitted MB" },
        {
          id: "isEnabled",
          label: "Enabled",
          type: "boolean",
        },
      ] as TableColumnTypes[],
    [],
  );

  const vpnConnectionOnClose = () => {
    missionControlRequestRef.current?.abort();
    missionControlRequestRef.current = null;
    setIsControlLaunching(false);
  };

  const openPasswordReset = () => {
    setIsControlLaunching(false);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const errorPopupOnClose = () => {
    setIsError(false);
  };

  const deviceDetail = useMemo(() => {
    const firstDevice = detailData?.deviceData?.[0];
    if (firstDevice) {
      return {
        ...firstDevice,
        lanIpAddress: convertIpAddress(firstDevice.lanIpAddress || ""),
      };
    }
    return null;
  }, [detailData]);

  const {
    data: pendingActions,
    isLoading: isLoadingActions,
    mutate: mutatePendingActions,
    error: pendingActionError,
  } = useRequest(`pendingActionsUIHook?serialNumber=${serialNumber}`);

  const pendingActionsData = useMemo(
    () =>
      pendingActions?.map((item: DeviceDetailDataTypes) => ({
        ...item,
        completed: item.completed ? "Yes" : "No",
      })),
    [pendingActions],
  );

  const goToLocation = () => {
    storeActiveSerial(serialNumber);
    navigate(`/map/${serialNumber}`);
  };

  const errorMessage = useMemo(() => {
    const messages: string[] = [];

    if (!isLoadingActions && pendingActionError) {
      messages.push(pendingActionError?.data?.error ?? pendingActionError?.message ?? "Network error");
    }

    if (!isGettingDetail && detailError) {
      messages.push(detailError?.data?.error ?? detailError?.message ?? "Network error");
    }

    const unique = Array.from(new Set(messages));
    return unique.length ? `Errors occurred while calling the API:\n${unique.join("\n")}` : null;
  }, [isLoadingActions, pendingActionError, isGettingDetail, detailError]);

  if (errorMessage) {
    return <ErrorScreen text={errorMessage} />;
  }

  const open = Boolean(actionsAnchorEl);
  const handleActionsClick = (event: { currentTarget: HTMLElement }) => {
    setActionsAnchorEl(event.currentTarget);
  };

  // DETAIL ACTIONS
  const handleUUIDReset = async (): Promise<void> => {
    const isFactoryReset = await getConfirmation({
      title: "Reset Cloud UUID",
      content:
        "Warning: If your UUID is showing in Mission Control>Expert Mode>System>MegaFi Configuration, do not proceed. If you do not have a UUID in Mission Control>Expert Mode>System>MegaFi Configuration, proceeding may fix your MegaPortal connection. If you are having further difficulty syncing your device to MegaPortal, please contact support@airgain.com",
      buttonTxt: ButtonText.Reset,
    });

    if (!isFactoryReset) {
      return;
    }

    setPopupTitle("Resetting Cloud UUID...");
    setIsLoading(true);

    try {
      await changeDeviceStatusFromUI({
        eiSerialNumber: deviceDetail.eiSerialNumber,
        resetCloudUUID: true,
      });
    } finally {
      setIsLoading(false);
      mutatePendingActions?.();
    }
  };

  const handleRefresh = async (): Promise<void> => {
    handleActionsClose();

    const isRefreshValues = await getConfirmation({
      title: "Refresh Values",
      content: "Are you sure to refresh values?",
      buttonTxt: ButtonText.Refresh,
    });
    if (!isRefreshValues) {
      return;
    }

    setPopupTitle("Refreshing values...");
    setIsLoading(true);

    let lastCheckInBefore = deviceDetail.lastCheckIn;

    try {
      const current = await getDeviceStatus(serialNumber).catch(() => null);
      lastCheckInBefore = current?.deviceData?.[0]?.lastCheckIn ?? lastCheckInBefore;

      await changeDeviceStatusFromUI({
        eiSerialNumber: deviceDetail.eiSerialNumber,
        getDeviceDetails: true,
      });
    } finally {
      setIsLoading(false);
      mutatePendingActions?.();
    }

    if (isMountedRef.current) {
      showInfo("Refresh command sent. Waiting for the device to report new values...");
    }

    setIsRefreshPolling(true);

    let isRefreshed = false;
    try {
      isRefreshed = await pollUntil(
        async () => {
          // stop polling after unmount
          if (!isMountedRef.current) {
            return true;
          }

          const fresh = await getDeviceStatus(serialNumber).catch(() => null);
          const lastCheckIn = fresh?.deviceData?.[0]?.lastCheckIn;
          return lastCheckIn !== undefined && lastCheckIn !== lastCheckInBefore;
        },
        REFRESH_VALUES_POLL_INTERVAL,
        REFRESH_VALUES_POLL_TIMEOUT,
      );
    } finally {
      setIsRefreshPolling(false);
    }

    if (!isMountedRef.current) {
      return;
    }

    if (isRefreshed) {
      mutateDetail?.();
      showSuccess("Device values refreshed.");
    } else {
      showWarning("The device has not reported new values yet. Values will update when it checks in.");
    }
  };

  const handleReboot = async (): Promise<void> => {
    handleActionsClose();

    const isRebootDevice = await getConfirmation({
      title: "Reboot Device",
      content: "Are you sure? You will lose connectivity while the device reboots.",
      buttonTxt: ButtonText.Reboot,
    });
    if (!isRebootDevice) {
      return;
    }

    setPopupTitle("Rebooting device...");
    setIsLoading(true);

    try {
      await changeDeviceStatusFromUI({
        eiSerialNumber: deviceDetail.eiSerialNumber,
        rebootDevice: true,
      });
    } finally {
      setIsLoading(false);
      mutatePendingActions?.();
    }
  };

  const handleLocationTrail = async (): Promise<void> => {
    handleActionsClose();

    const isConfirmed = await getConfirmation({
      title: "Location Tracking",
      content: `Are you sure you want to ${deviceDetail.locationTrailEnabled ? "disable" : "enable"} location tracking`,
      buttonTxt: `${deviceDetail.locationTrailEnabled ? ButtonText.Disable : ButtonText.Enable}`,
    });
    if (!isConfirmed) {
      return;
    }
    const data = deviceDetail.locationTrailEnabled
      ? { disableLocationTrail: true }
      : { enableLocationTrail: true, getDeviceDetails: true };

    setPopupTitle(`${deviceDetail.locationTrailEnabled ? "Disabling" : "Enabling"} location trail...`);
    setIsLoading(true);

    try {
      await changeDeviceStatusFromUI({
        eiSerialNumber: deviceDetail.eiSerialNumber,
        ...data,
      });
    } finally {
      setIsLoading(false);
      mutatePendingActions?.();
      mutateDetail?.();
    }
  };

  const launchMissionControl = async () => {
    handleActionsClose();
    if (!serialNumber || missionControlRequestRef.current) return;
    const controller = new AbortController();
    missionControlRequestRef.current = controller;
    setMissionControlStatus(undefined);
    setIsError(false);
    setIsControlLaunching(true);
    try {
      await connectMissionControl(serialNumber, {
        signal: controller.signal,
        onStatus: (status) => {
          if (!controller.signal.aborted && isMountedRef.current) setMissionControlStatus(status);
        },
      });
      if (controller.signal.aborted || !isMountedRef.current) return;
      sessionStorage.setItem("serialNumber", serialNumber);
      navigate(`/launch-mission-control/${serialNumber}`);
    } catch (error: any) {
      if (controller.signal.aborted || !isMountedRef.current) return;
      reportError("pages/Devices/Detail/device-detail.page.launchMissionControl", error);
      setIsError(true);
      setErrorText(missionControlErrorMessage(error));
    } finally {
      if (missionControlRequestRef.current === controller) {
        missionControlRequestRef.current = null;
        if (isMountedRef.current) setIsControlLaunching(false);
      }
    }
  };

  const handleFactoryReset = async (): Promise<void> => {
    setIsOpen(false);

    const response = await factoryResetSpeedwayDevice(deviceDetail.eiSerialNumber);
    if (response && response.status === "Success") {
      showWarning("Factory reset command sent successfully.");
    }
  };

  const handleResetPassword = async (): Promise<void> => {
    await resetSpeedwayPassword(deviceDetail.eiSerialNumber);
    setIsOpen(false);
  };

  const passwordResetOnClick = () => {
    setIsOpen(true);
    handleActionsClose();

    setPopupTitle("Reset Password");
    setPopupBody("Are you sure you want to reset the device password?");
    setPopupBtnFunc(() => handleResetPassword);
  };

  const factoryResetOnClick = () => {
    setIsOpen(true);
    handleActionsClose();

    setPopupTitle("Factory Reset Device");
    setPopupBody(
      "Are you sure you want to factory reset the device? You will lose connectivity while the device resets",
    );
    setPopupBtnFunc(() => handleFactoryReset);
  };

  const getColumnValue = (value: string | number | boolean, columnId: string) => {
    const actionValues: { value: string | number | boolean } = { value };

    if (columnId === "isEnabled") {
      return <CheckIcon />;
    }

    // The pending action comes over as a long string. We break it up with the following
    if (columnId === ACTION_JSON && typeof actionValues.value === "string") {
      if (actionValues.value.includes("Date=")) {
        actionValues.value.replace("Date=", "");

        const formatedDate = getDateString(actionValues.value);

        return formatedDate;
      }

      const regex = /"[^"\\]*(?:\\[\s\S][^"\\]*)*"/g;
      const actionsArray = actionValues.value.match(regex);
      const actionIndex = actionsArray?.indexOf("ei.action");
      const pendingAction = actionIndex && actionsArray && actionsArray[actionIndex + 2].replaceAll('"', "");

      return pendingAction;
    }
    return value;
  };

  const getMissionControlMenuItem = () => {
    if (!deviceDetail?.allowVPNConnection || !deviceDetail?.online) {
      return <div />;
    }

    return <MenuItem onClick={launchMissionControl}>Launch Mission Control</MenuItem>;
  };

  return (
    <DetailWrapper>
      <ActionBar style={{ marginBottom: 0 }}>
        <NxtIconButton onClick={() => goToLocation()} text="Show Location" icon={<LocationOnIcon />} />
        {deviceDetail?.eiSoftwareVersion.startsWith("MegaFi_2") && (
          <NxtIconButton
            icon={<OpenInNewIcon />}
            text="Release Notes"
            onClick={() => window.open(RELEASE_NOTES_URL, "_blank", "noopener,noreferrer")}
          />
        )}

        {!isDemoUser && (
          <NxtIconButton
            id="device-actions-button"
            aria-controls={open ? "device-actions-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            icon={<MoreVertIcon />}
            onClick={handleActionsClick}
            text="Options"
          />
        )}

        <Menu
          id="device-actions-menu"
          aria-labelledby="device-actions-button"
          anchorEl={actionsAnchorEl}
          open={open}
          onClose={handleActionsClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuItem
            onClick={handleRefresh}
            disabled={!deviceDetail?.online || !deviceDetail?.allowVPNConnection || isRefreshPolling}
          >
            Refresh Values
          </MenuItem>
          {!isReadOnly && !isDemoUser && deviceDetail?.eiSoftwareVersion?.startsWith("MegaFi_2") && (
            <MenuItem
              onClick={() => {
                handleActionsClose();
                setIsOpenArchivesDialog(true);
              }}
            >
              Configuration Archives
            </MenuItem>
          )}
          <MenuItem onClick={handleReboot} disabled={!deviceDetail?.online}>
            Reboot Device
          </MenuItem>
          <MenuItem onClick={handleLocationTrail}>
            {" "}
            {deviceDetail?.locationTrailEnabled ? "Disable" : "Enable"} Location Tracking
          </MenuItem>
          {getMissionControlMenuItem()}
          <MenuItem onClick={passwordResetOnClick} disabled={!deviceDetail?.online || !deviceDetail?.allowReset}>
            Reset Password
          </MenuItem>
          <MenuItem onClick={factoryResetOnClick} disabled={!deviceDetail?.online || !deviceDetail?.allowReset}>
            Factory Reset
          </MenuItem>
        </Menu>
        <ConfigurationArchivesDialog
          open={isOpenArchivesDialog}
          onClose={() => setIsOpenArchivesDialog(false)}
          targetSerialNumbers={serialNumber ? [serialNumber] : []}
        />
        <Confirmation />
      </ActionBar>

      <PageSection>
        <DetailForm
          deviceDetail={deviceDetail}
          mutatePendingActions={mutatePendingActions}
          setIsLoadingDetail={setIsLoadingDetail}
          isLoadingDetail={isLoadingDetail || isGettingDetail}
          mutateDetail={mutateDetail}
        />
      </PageSection>

      <PageSection>
        <SectionHeader>
          <span className="header-title">Device Interfaces</span>
        </SectionHeader>

        <CustomTable>
          <CustomTable.Header columns={columns} />

          <CustomTable.Body isLoading={isGettingDetail} deviceData={deviceDetail?.eiInterfaces}>
            {deviceDetail?.eiInterfaces?.map((row: any, i: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={`${row.eiInterfaces}${i}`}>
                {columns.map((column: any) => {
                  const value = getColumnValue(row[column.id as keyof DeviceDetailDataTypes], column.id);
                  return (
                    <TableBodyCell key={column.id} align={column.align} style={{ color: "var(--mega-color-gray500)" }}>
                      {getTableCellValue(column, value)}
                    </TableBodyCell>
                  );
                })}
              </TableRow>
            ))}
          </CustomTable.Body>
        </CustomTable>
      </PageSection>

      <PageSection>
        <SectionHeader>
          <span className="header-title">Device Event Log</span>
        </SectionHeader>

        <CustomTable>
          <CustomTable.Header
            columns={
              deviceDetail?.eiSoftwareVersion.startsWith("MegaFi_2") ? EVENTLOG_GRID_COLUMNS : PENDING_GRID_COLUMNS
            }
          />

          <CustomTable.Body isLoading={isLoadingActions} deviceData={pendingActionsData}>
            {pendingActionsData?.map((row: DeviceDetailDataTypes, i: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={`${row.eiSerialNumber}${i}`}>
                {PENDING_GRID_COLUMNS.map((column: PendingGridColumnsTypes) => {
                  const value = getColumnValue(row[column.id as keyof DeviceDetailDataTypes], column.id);

                  return (
                    <TableBodyCell key={column.id} align={column.align} style={{ color: "var(--mega-color-gray500)" }}>
                      {column.format && typeof value === "number" ? column.format(value) : value}
                    </TableBodyCell>
                  );
                })}
              </TableRow>
            ))}
          </CustomTable.Body>
        </CustomTable>
      </PageSection>

      <Popup
        title="Establishing VPN connection...Please wait."
        open={isControlLaunching}
        primaryBtn={{ onClick: vpnConnectionOnClose, text: "Cancel" }}
      >
        <Typography>Waiting for the VPN connection. This can take up to 5 minutes.</Typography>
        {missionControlStatus && <Typography>VPN status: {missionControlStatus.vpnStatus}</Typography>}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "32px" }}>
          <CircularProgress size={60} variant="indeterminate" />
        </div>
      </Popup>

      <Popup title="Error" open={isError} primaryBtn={{ onClick: errorPopupOnClose, text: "Cancel" }}>
        <Typography>{errorText}</Typography>
        <MissionControlHelp status={missionControlStatus} variant="dialog" />
      </Popup>

      <Popup
        title={popupTitle}
        open={isPasswordReset}
        primaryBtn={{ onClick: popupBtnFunc!, text: ButtonText.Reset }}
        secondaryBtn={{ onClick: () => setIsOpen(false), text: ButtonText.Cancel }}
      >
        <Typography>{popupBody}</Typography>
      </Popup>

      <Popup title={popupTitle} open={isLoading}>
        <Spinner />
      </Popup>
    </DetailWrapper>
  );
}
